import { useState } from 'react'
import { formatMoneyCents as fmtCents } from '../utils/money'
import { useI18n } from '../i18n'
import { roleCopayCents } from '../utils/copayFigures'
import { eligibilityCopy } from '../utils/eligibilityCopy'
import { pickLevelSpend, LEVEL_KEYS } from '../utils/coverageLevels'
import { limitNoticeKey, recheckBlocked, limitNoticeParams, verdictWithheld } from '../utils/eligibilityLimit'
import { Check, AlertCircle, Shield, Search, ScanLine, Pencil, RefreshCw, ChevronRight, Clock } from 'lucide-react'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * EligibilityBadge — Layered result-class component, brand-locked colors only.
 *
 * v222.4.11 — Replaced the 4-state binary model with a result-class model
 * sourced from `eligibility.resultClass` (falls back to legacy `eligibility.status`).
 *
 * v227.1.0 — Visual revision (approved mockup) + PCP/specialist copay split:
 *   • White card body + 1.5px state-color border + 36px icon chip (OrellaPageHeader
 *     chip pattern) replaces the flat full-color wash.
 *   • Per-class status pill in the header for instant triage.
 *   • Active state shows in-network / plan-type meta line.
 *   • Actionable states (not_found / data_mismatch) get a filled primary
 *     "Re-scan card" CTA + ghost "Edit member ID".
 *   • Copay display is role-aware:
 *       - doctorRole === 'specialist' | 'pcp'  → single role-appropriate copay
 *         (used on doctor-scoped surfaces: Share confirm, booking, check-in)
 *       - doctorRole absent (general check)    → BOTH PCP + Specialist copays
 *         (used on Profile → Insurance, where no doctor is selected yet)
 *
 * Props:
 *   eligibility      — last_eligibility object (null/undefined → renders nothing)
 *   variant          — 'compact' | 'detailed' (default 'compact')
 *   doctorRole       — 'specialist' | 'pcp' | null|undefined (see above)
 *   onRecheck        — optional callback; renders "Re-check coverage" link
 *   onRescanCard     — optional 'Re-scan card' CTA (not_found / data_mismatch)
 *   onEditMemberId   — optional 'Edit member ID' CTA (not_found / data_mismatch)
 *   loading          — show pending state while a re-check is firing
 *   limitNotice      — PROD-691 (P4-3), optional { kind: 'limit' | 'unavailable', availableAt }
 *                      from utils/eligibilityLimit.js limitNoticeOf: the server answered a check
 *                      with its daily limit and the STORED result. Detailed / comprehensive only:
 *                      one line under the head line; Re-check is not offered until availableAt.
 *                      Drawn only when the UI language has approved text for it (under es,
 *                      until Dr. Rami approves: no line, and Re-check stays as today).
 *                      Q-C5: { kind: 'otherId', availableAt } (limitedResultFor) is a CHANGED
 *                      member ID at the limit: no verdict at all, whatever `eligibility` holds,
 *                      only "You can check this member ID again after {{time}}." (es: nothing).
 *                      PROD-936: { kind: 'reused', checkedAt, nextCheckAt } is the server's 30-day
 *                      reuse of the stored result: "Checked {{when}} - this is that result. You can
 *                      check again after {{date}}, ..." and no Re-check until nextCheckAt.
 *   showAge          — PROD-936 PAT-2, compact only: "as of {{when}}" after the head line (the
 *                      stored result may be up to 30 days old; ShareScreen's confirm-doctor badge).
 */

const NAVY  = '#15354F'
const BLUE  = '#46A8F9'
const LIGHT = '#A7D9FC'
const ICE   = '#E9F8FF'
const GREEN = '#66D170'
const SLATE = '#94A3B8'
const GREEN_INK = '#2F9E4A'
const SLATE_INK = '#64748B'
const AMBER = '#D9912B'

// Specialties treated as primary-care for copay selection. Everything else
// (ENT/Otolaryngology, Cardiology, Dermatology, …) resolves to 'specialist'.
const PCP_SPECIALTIES = [
  'family medicine', 'family practice', 'internal medicine', 'general practice',
  'general practitioner', 'primary care', 'pediatric', 'geriatric',
]

// Map a doctor's specialty string → copay role. Returns null when unknown,
// which the badge treats as a general (both-copays) check.
export function roleFromSpecialty(specialty) {
  if (!specialty) return null
  const s = String(specialty).toLowerCase()
  return PCP_SPECIALTIES.some(p => s.includes(p)) ? 'pcp' : 'specialist'
}

// Legacy-status → resultClass bridge for cached payloads written before v222.4.11.
function deriveResultClass(eligibility) {
  if (!eligibility) return null
  const rc = (eligibility.resultClass || '').toLowerCase()
  if (rc) return rc

  const s = (eligibility.status || '').toLowerCase()
  if (s === 'active' || s === 'verified') return 'active'
  if (s === 'inactive' || s === 'inactive_coverage') return 'inactive'
  if (s === 'self_pay' || s === 'selfpay') return 'self_pay'
  if (s === 'pending' || s === 'checking') return 'pending'
  if (s === 'error' || s === 'system_error') return 'unknown'
  if (s === 'unknown') return 'unknown'
  return eligibility.checkedAt ? 'unknown' : null
}

// ★ PROD-431 (4), patient push 3a: the stamp, the status pills and the fallback headlines go
//   through t() (en + es). Under es they were English beside the API's Spanish headline.
export function fmtRelative(iso, t) {
  if (!iso) return ''
  try {
    const then = new Date(iso)
    const diffMin = Math.round((Date.now() - then.getTime()) / 60000)
    if (diffMin < 1) return t('eligibility.justNow')
    if (diffMin < 60) return t('eligibility.minAgo', { n: diffMin })
    const diffHr = Math.round(diffMin / 60)
    if (diffHr < 24) return t('eligibility.hrAgo', { n: diffHr })
    const diffDay = Math.round(diffHr / 24)
    return t(diffDay === 1 ? 'eligibility.dayAgo' : 'eligibility.daysAgo', { n: diffDay })
  } catch { return '' }
}

// Build the copay tiles for the active state based on doctor role.
//   specialist / pcp → [single role copay]
//   general          → [PCP, Specialist] (or single generic if payer gave neither split)
function copayTilesFor(eligibility, doctorRole) {
  const spec = eligibility?.copaySpecialistCents
  const pcp  = eligibility?.copayPcpCents
  const gen  = eligibility?.copayCents
  if (doctorRole === 'specialist') return [{ labelKey: 'eligibility.specialistCopay', cents: roleCopayCents(eligibility, 'specialist') }]
  if (doctorRole === 'pcp')        return [{ labelKey: 'eligibility.pcpCopay',        cents: roleCopayCents(eligibility, 'pcp') }]
  if (pcp != null || spec != null) {
    return [
      { labelKey: 'eligibility.pcpCopay',        cents: pcp  ?? null },
      { labelKey: 'eligibility.specialistCopay', cents: spec ?? null },
    ]
  }
  // ★ r1 repair: the general-copay tile carries a key like the others. It kept the English
  //   { label: 'Copay' } when the rest moved to labelKey, so a plan with only copayCents and no
  //   doctorRole rendered t(undefined) and the badge threw.
  return [{ labelKey: 'eligibility.copay', cents: gen ?? null }]
}

// TEST 3.10 — the deductible / out-of-pocket figures come from utils/coverageLevels.js, which
// returns them WITH their coverage level (individual, else family) and never pairs a total and
// a remaining from different levels. The flat top-level fields carry no level and are not read.

// ★ PROD-583 (Alex 2026-10-05 V-3, V-4): nothing re-checks coverage on a schedule (the API's
//   AUTO_RECHECK_INTERVAL_DAYS is read by nothing, and level 1 adds no scheduler), so the
//   comprehensive card has no "Refreshes quarterly" footer and never warns that a result is old.
//   When the check ran is the header's "as of <relative>" only (review B1: a footer date read in
//   Spanish as "a partir de <date>", i.e. coverage starting then, and doubled the header).

// Per-class palette. `border` is the state color; `chipBg` tints the icon chip;
// `pill` is the header status chip. Headline + body come from the API when
// present (eligibility.headline / patientMessage, or headlineEs / patientMessageEs for an
// es locale, through utils/eligibilityCopy.js); fallbacks for legacy payloads.
export function paletteFor(resultClass, t) {
  switch (resultClass) {
    case 'active':
      return {
        border: GREEN, chipBg: 'rgba(102,209,112,0.14)',
        icon: <Check size={18} style={{ color: GREEN }} strokeWidth={2.6} />,
        iconSm: <Check size={12} style={{ color: GREEN }} strokeWidth={3} />,
        pill: { text: t('eligibility.pillVerified'), bg: 'rgba(102,209,112,0.16)', fg: NAVY },
        fallbackHeadline: t('eligibility.fbVerified'),
      }
    case 'inactive':
      return {
        border: NAVY, chipBg: ICE,
        icon: <AlertCircle size={18} style={{ color: NAVY }} />,
        iconSm: <AlertCircle size={12} style={{ color: NAVY }} />,
        pill: { text: t('eligibility.pillPlanEnded'), bg: ICE, fg: NAVY },
        fallbackHeadline: t('eligibility.fbInactive'),
      }
    case 'not_found':
      return {
        border: SLATE, chipBg: '#F1F5F9',
        icon: <Search size={18} style={{ color: SLATE }} />,
        iconSm: <Search size={12} style={{ color: SLATE }} />,
        pill: { text: t('eligibility.pillActionNeeded'), bg: '#F1F5F9', fg: NAVY },
        fallbackHeadline: t('eligibility.fbNotFound'),
      }
    case 'data_mismatch':
      return {
        border: SLATE, chipBg: '#F1F5F9',
        icon: <AlertCircle size={18} style={{ color: SLATE }} />,
        iconSm: <AlertCircle size={12} style={{ color: SLATE }} />,
        pill: { text: t('eligibility.pillQuickCheck'), bg: '#F1F5F9', fg: NAVY },
        fallbackHeadline: t('eligibility.fbDataMismatch'),
      }
    case 'payer_unreachable':
      return {
        border: LIGHT, chipBg: ICE,
        icon: <RefreshCw size={18} style={{ color: BLUE }} />,
        iconSm: <RefreshCw size={12} style={{ color: BLUE }} />,
        pill: { text: t('eligibility.pillTemporary'), bg: ICE, fg: BLUE },
        fallbackHeadline: t('eligibility.fbUnreachable'),
      }
    case 'config_issue':
    case 'needs_more_info':
    case 'payer_not_mapped':
    case 'unknown':
      return {
        border: SLATE, chipBg: '#F1F5F9',
        icon: <AlertCircle size={18} style={{ color: SLATE }} />,
        iconSm: <AlertCircle size={12} style={{ color: SLATE }} />,
        pill: { text: resultClass === 'needs_more_info' ? t('eligibility.pillMoreInfo')
              : resultClass === 'payer_not_mapped' ? t('eligibility.pillSettingUp') : t('eligibility.pillUnavailable'),
                bg: '#F1F5F9', fg: NAVY },
        fallbackHeadline: t('eligibility.fbUnavailable'),
      }
    case 'self_pay':
      return {
        border: LIGHT, chipBg: ICE,
        icon: <Shield size={18} style={{ color: BLUE }} />,
        iconSm: <Shield size={12} style={{ color: BLUE }} />,
        pill: { text: t('eligibility.pillSelfPay'), bg: ICE, fg: BLUE },
        fallbackHeadline: t('eligibility.fbSelfPay'),
      }
    case 'pending':
    default:
      return {
        border: LIGHT, chipBg: ICE,
        icon: <OrellaSpinner size={18} />,
        iconSm: <OrellaSpinner size={16} />,
        pill: { text: t('eligibility.pillChecking'), bg: ICE, fg: BLUE },
        fallbackHeadline: t('eligibility.fbChecking'),
      }
  }
}

// ★★★ v242.180.0 — THIS COMPONENT HAD NO t() AT ALL, and it renders the numbers a
//   patient reads before they decide whether they can afford a visit: copays, coinsurance,
//   in/out of network, when the plan ends. Sixteen strings, none of which any locale file
//   could ever have carried.
export default function EligibilityBadge({
  eligibility,
  variant = 'compact',
  comprehensive = false,
  doctorRole,
  onRecheck,
  onRescanCard,
  onEditMemberId,
  loading = false,
  limitNotice = null,
  showAge = false,
}) {
  const { t, language, hasText } = useI18n()
  // Expander state for the comprehensive ProfileInsurance card.
  const [showAllBenefits, setShowAllBenefits] = useState(false)
  // Pending state takes priority
  if (loading) {
    return (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
           style={{ background: ICE, border: `1.5px solid ${LIGHT}` }}>
        <OrellaSpinner size={16} />
        <span className="text-[11px] font-semibold" style={{ color: NAVY }}>
          {t('eligibility.verifyingCoverage', 'Verifying coverage…')}
        </span>
      </div>
    )
  }

  // ★ PROD-691 (P4-3): the limit notice, and Re-check withheld until the next allowed check.
  const noticeKey = limitNoticeKey(limitNotice)
  const showNotice = !!noticeKey && typeof hasText === 'function' && hasText(noticeKey)
  const recheck = showNotice && recheckBlocked(limitNotice) ? null : onRecheck
  const noticeEl = showNotice ? (
    <p data-limit-notice={limitNotice.kind} className="text-[11.5px] mt-2 leading-snug flex items-start gap-1.5" style={{ color: SLATE_INK }}>
      <Clock size={12} className="shrink-0 mt-[2px]" style={{ color: AMBER }} />
      <span>{t(noticeKey, limitNoticeParams(limitNotice, language === 'es' ? 'es-US' : 'en-US', (iso) => fmtRelative(iso, t)))}</span>
    </p>
  ) : null

  // ★ Q-C5 (Alex 2026-10-07): a changed member ID at the limit. The stored result was checked for
  //   another ID, so NO verdict is drawn, whatever `eligibility` holds: only when this ID can be
  //   checked. Under es (no approved Spanish yet) nothing at all.
  if (verdictWithheld(limitNotice)) return noticeEl

  const resultClass = deriveResultClass(eligibility)
  if (!resultClass) return null

  const palette = paletteFor(resultClass, t)
  // ★ L1 step 5b (TEST 3.05): the API's Spanish when the UI is es and the class has it.
  const copy = eligibilityCopy(eligibility, language)
  const headline = copy.headline || palette.fallbackHeadline
  const body = copy.body  // null for legacy payloads — that's fine
  const action = eligibility?.suggestedAction
  const showActionableCtas = action === 'verify_member_id' || action === 'verify_demographics'
  const isInfoState = resultClass === 'payer_unreachable' || resultClass === 'config_issue'
    || resultClass === 'needs_more_info' || resultClass === 'payer_not_mapped'
    || resultClass === 'unknown'

  // ── Compact variant ──
  if (variant === 'compact') {
    // Doctor-scoped surfaces use compact; pick the role copay (fallback generic).
    // PROD-976: the role's own figure only (utils/copayFigures.js), never the other role's roll-up.
    const compactCopay = roleCopayCents(eligibility, doctorRole === 'pcp' ? 'pcp' : 'specialist')
    return (
      <div className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full"
           style={{ background: '#FFFFFF', border: `1.5px solid ${palette.border}` }}>
        <span className="w-5 h-5 rounded-[7px] flex items-center justify-center shrink-0"
              style={{ background: palette.chipBg, border: `1px solid ${palette.border}` }}>
          {palette.iconSm}
        </span>
        <span className="text-[11px] font-semibold" style={{ color: NAVY }}>{headline}</span>
        {resultClass === 'active' && compactCopay !== null && compactCopay !== undefined && (
          <span className="text-[11px] font-medium" style={{ color: NAVY, opacity: 0.6 }}>
            · {t('eligibility.copayAmount', { amount: fmtCents(compactCopay) })}
          </span>
        )}
        {showAge && eligibility?.checkedAt && (
          <span data-as-of className="text-[10.5px] font-medium" style={{ color: SLATE_INK }}>
            · {t('eligibility.asOf', { when: fmtRelative(eligibility.checkedAt, t) || '—' })}
          </span>
        )}
      </div>
    )
  }

  // ── Comprehensive variant (ProfileInsurance only) — in-network status snapshot ──
  // Overrides ONLY the active state; non-active states fall through to the detailed
  // rendering below so error/inactive/not-found copy stays intact.
  if (variant === 'detailed' && comprehensive && resultClass === 'active') {
    const ded = pickLevelSpend(eligibility, 'deductible')
    const oopN = pickLevelSpend(eligibility, 'outOfPocket')
    const planName = eligibility?.planIdentifiers?.planName
    const networkName = eligibility?.planIdentifiers?.networkName
    const groupNumber = eligibility?.planIdentifiers?.groupNumber
    const specCents = roleCopayCents(eligibility, 'specialist')
    const pcpCents = eligibility?.copayPcpCents // own figure only; a flat plan's generic copay is the Specialist tile (PROD-976)
    const coins = eligibility?.coinsurancePercent
    const planEnd = eligibility?.coverageDates?.planEnd
    const referralReq = eligibility?.referral?.required === true
    const memberConfirmed = !!eligibility?.subscriberConfirmed
    // Extra per-service benefits for the expander (exclude the two shown as copay tiles).
    const extraBenefits = (Array.isArray(eligibility?.benefits) ? eligibility.benefits : [])
      .filter(b => b && b.serviceType && !/^office visit$|^specialist$/i.test(String(b.serviceType).trim()) && b.copayCents != null)
    const hasFullBenefits = extraBenefits.length > 0 || coins != null || networkName || groupNumber || planEnd || memberConfirmed

    const renderMeter = (label, node) => {
      if (!node || (node.total == null && node.remaining == null)) return null
      const { total, remaining } = node
      // TEST 3.10: the level the figures belong to, on every meter
      const levelChip = <span className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ color: NAVY, background: '#EAF1F8' }}>{t(LEVEL_KEYS[node.level])}</span>
      // ★ PROD-682: the network chip names the bucket the figures came from (coverageLevels.js S1):
      //   'in' -> "In-network"; 'out' -> "Out of network" (the key this card already uses for an
      //   out-of-network plan); 'unknown' (the payer line carries no in-network flag Y / N) -> no
      //   network chip, because nothing says which network the figure is for. It said "In-network"
      //   for all three.
      const networkChip = node.network === 'in'
        ? <span className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ color: GREEN_INK, background: 'rgba(102,209,112,0.14)' }}>{t('doctors.inNetwork')}</span>
        : node.network === 'out'
          ? <span className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ color: SLATE_INK, background: '#F1F5F9' }}>{t('eligibility.outOfNetwork')}</span>
          : null
      const header = (
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider font-extrabold" style={{ color: SLATE }}>{label}</span>
          <span className="flex items-center gap-1">{levelChip}{networkChip}</span>
        </div>
      )
      if (total == null) {
        return (
          <div key={label}>
            {header}
            <div className="text-[18px] font-extrabold mt-1" style={{ color: NAVY, letterSpacing: '-0.02em' }}>
              {fmtCents(remaining)} <span className="text-[11.5px] font-semibold" style={{ color: SLATE_INK }}>{t('eligibility.remaining')}</span>
            </div>
          </div>
        )
      }
      // ★ PROD-684: a total with NO remaining says nothing about what is met. The bucket reported
      //   the plan's total only, so the meter shows the total only: no "met" amount, no "Fully
      //   met", no percentage, no bar. (Before, a missing remaining was read as 0 and the meter
      //   said "$1500 met / Fully met / 100%"; a total of 0 said "$0 met / Fully met / 0% of $0".)
      //   A remaining of 0 is a real figure and still takes the full meter below.
      if (remaining == null) {
        return (
          <div key={label}>
            {header}
            <div className="text-[18px] font-extrabold mt-1" style={{ color: NAVY, letterSpacing: '-0.02em' }}>
              {fmtCents(total)}
            </div>
          </div>
        )
      }
      const met = Math.max(0, total - remaining)
      const pct = total > 0 ? Math.min(100, Math.round((met / total) * 100)) : 0
      return (
        <div key={label}>
          {header}
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-[19px] font-extrabold" style={{ color: NAVY, letterSpacing: '-0.02em' }}>
              {fmtCents(met)} <span className="text-[11.5px] font-semibold" style={{ color: SLATE_INK }}>{t('eligibility.met')}</span>
            </span>
            <span className="text-[11.5px] font-bold" style={{ color: NAVY }}>
              {fmtCents(remaining)} <span style={{ color: SLATE, fontWeight: 600 }}>{t('eligibility.toGo')}</span>
            </span>
          </div>
          <div className="h-[9px] rounded-full mt-2 overflow-hidden" style={{ background: '#EAF1F8' }}>
            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#46A8F9,#66D170)' }} />
          </div>
          <div className="text-[10px] font-bold mt-1" style={{ color: SLATE }}>{t('eligibility.pctOf', { pct, total: fmtCents(total) })}</div>
        </div>
      )
    }

    const dMeter = renderMeter(t('eligibility.deductible'), ded)
    const oMeter = renderMeter(t('eligibility.outOfPocketMax'), oopN)

    return (
      <div className="rounded-[18px] p-4" style={{ background: '#FFFFFF', border: `1.5px solid ${GREEN}`, boxShadow: '0 1px 3px rgba(21,53,79,0.06)' }}>
        {/* Header */}
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center shrink-0" style={{ background: 'rgba(102,209,112,0.14)', border: `1.5px solid ${GREEN}` }}>
              <Check size={18} strokeWidth={3} style={{ color: GREEN_INK }} />
            </span>
            <div className="min-w-0">
              <div className="text-[14px] font-extrabold leading-tight" style={{ color: NAVY }}>{t('eligibility.coverageActive')}</div>
              {(planName || eligibility?.planType) && (
                <div className="text-[11.5px] font-semibold truncate" style={{ color: SLATE_INK }}>
                  {[planName, eligibility?.planType ? String(eligibility.planType).toUpperCase() : null].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </div>
          <div className="text-[10.5px] text-right shrink-0" style={{ color: SLATE }}>
            {t('eligibility.asOf', { when: fmtRelative(eligibility?.checkedAt, t) || '—' })}
            {recheck && (
              <button onClick={recheck} className="block mt-1 ml-auto text-[11px] font-bold" style={{ color: BLUE }}>{t('eligibility.recheck')}</button>
            )}
          </div>
        </div>
        {noticeEl}

        {/* Status meters (hero) */}
        {(dMeter || oMeter) && (
          <div className="mt-4 flex flex-col gap-3.5">
            {dMeter}
            {oMeter}
          </div>
        )}

        {/* Visit copays */}
        {(specCents != null || pcpCents != null) && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider font-extrabold mb-1.5" style={{ color: SLATE }}>{t('eligibility.visitCopays', 'Visit copays')}</div>
            <div className="grid grid-cols-2 gap-2">
              {specCents != null && (
                <div className="rounded-[11px] px-2.5 py-2" style={{ background: ICE }}>
                  <div className="text-[8.5px] uppercase tracking-wider font-extrabold" style={{ color: SLATE }}>{t('diagnose.specialist', 'Specialist')}</div>
                  <div className="text-[16px] font-extrabold mt-0.5" style={{ color: NAVY, letterSpacing: '-0.02em' }}>{fmtCents(specCents)}</div>
                </div>
              )}
              {pcpCents != null && (
                <div className="rounded-[11px] px-2.5 py-2" style={{ background: ICE }}>
                  <div className="text-[8.5px] uppercase tracking-wider font-extrabold" style={{ color: SLATE }}>{t('eligibility.officeVisit', 'Office visit')}</div>
                  <div className="text-[16px] font-extrabold mt-0.5" style={{ color: NAVY, letterSpacing: '-0.02em' }}>{fmtCents(pcpCents)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* View full benefits */}
        {hasFullBenefits && (
          <div className="mt-3.5 pt-3" style={{ borderTop: '1px solid #EEF2F6' }}>
            <button onClick={() => setShowAllBenefits(v => !v)} className="w-full flex items-center justify-between text-[12px] font-bold" style={{ color: BLUE }}>
              <span>{t('eligibility.viewFullBenefits', 'View full benefits')}</span>
              <ChevronRight size={15} className={`transition-transform ${showAllBenefits ? 'rotate-90' : ''}`} />
            </button>
            {showAllBenefits && (
              <div className="mt-2.5">
                {extraBenefits.map((b, i) => {
                  const free = b.copayCents === 0
                  return (
                    <div key={i} className="flex items-center justify-between py-1.5 text-[12px]" style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <span className="font-semibold" style={{ color: SLATE_INK }}>{b.serviceType}</span>
                      <span className="font-bold" style={{ color: free ? GREEN_INK : NAVY }}>{free ? t('eligibility.noCopay') : fmtCents(b.copayCents)}</span>
                    </div>
                  )
                })}
                {coins != null && (
                  <div className="flex items-center justify-between py-1.5 text-[12px]" style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <span className="font-semibold" style={{ color: SLATE_INK }}>{t('eligibility.coinsurance', 'Coinsurance')}</span>
                    {/* PROD-584: the 271 reports the percentage, not when it applies; no "after deductible" */}
                    <span data-coinsurance className="font-bold" style={{ color: NAVY }}>{`${coins}%`}</span>
                  </div>
                )}
                {referralReq && (
                  <div className="flex items-center justify-between py-1.5 text-[12px]" style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <span className="font-semibold" style={{ color: SLATE_INK }}>{t('eligibility.referralRequired', 'Referral required')}</span>
                    <span className="font-bold" style={{ color: NAVY }}>{t('common.yes', 'Yes')}{eligibility?.planType ? ` (${String(eligibility.planType).toUpperCase()})` : ''}</span>
                  </div>
                )}
                {networkName && (
                  <div className="flex items-center justify-between py-1.5 text-[12px]" style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <span className="font-semibold" style={{ color: SLATE_INK }}>{t('eligibility.network', 'Network')}</span>
                    <span className="font-bold" style={{ color: NAVY }}>{networkName}</span>
                  </div>
                )}
                {groupNumber && (
                  <div className="flex items-center justify-between py-1.5 text-[12px]" style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <span className="font-semibold" style={{ color: SLATE_INK }}>{t('cob.groupNumber')}</span>
                    <span className="font-bold" style={{ color: NAVY }}>{groupNumber}</span>
                  </div>
                )}
                {planEnd && (
                  <div className="flex items-center justify-between py-1.5 text-[12px]" style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <span className="font-semibold" style={{ color: SLATE_INK }}>{t('eligibility.coveragePeriod', 'Coverage period')}</span>
                    <span className="font-bold" style={{ color: NAVY }}>{t('eligibility.through', 'Through')} {planEnd}</span>
                  </div>
                )}
                {memberConfirmed && (
                  <div className="flex items-center justify-between py-1.5 text-[12px]">
                    <span className="font-semibold" style={{ color: SLATE_INK }}>{t('onboarding.insurance.member', 'Member')}</span>
                    <span className="font-bold" style={{ color: GREEN_INK }}>{t('caseHome.confirmed', 'Confirmed')}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* PROD-583: no footer line. The header already says when the check ran ("as of 2 hours
            ago") and offers Re-check; the old footer claimed "Refreshes quarterly" or warned of age. */}
      </div>
    )
  }

  // ── Detailed variant ──
  // TEST 3.10: one figure per tile (the remaining, else the total), from ONE level, labelled with it
  const dedL = pickLevelSpend(eligibility, 'deductible')
  const oopL = pickLevelSpend(eligibility, 'outOfPocket')
  const ded = dedL ? (dedL.remaining ?? dedL.total) : null
  const oop = oopL ? (oopL.remaining ?? oopL.total) : null
  const cTiles = copayTilesFor(eligibility, doctorRole)
  const twoCopays = cTiles.length === 2
  // ★ PROD-475: the tiles carry i18n KEYS, rendered with t() (en + es). They were English
  //   literals, so a Spanish patient read "PCP copay", "Deductible", "OOP max" on a Spanish card.
  const tiles = [...cTiles, { labelKey: 'eligibility.deductible', cents: ded, levelKey: dedL && LEVEL_KEYS[dedL.level] }, { labelKey: 'eligibility.oopMax', cents: oop, levelKey: oopL && LEVEL_KEYS[oopL.level] }]

  return (
    <div className="rounded-[16px] p-3.5"
         style={{ background: '#FFFFFF', border: `1.5px solid ${palette.border}`,
                  boxShadow: '0 1px 2px rgba(21,53,79,0.05)' }}>
      {/* Header: icon chip + headline + status pill, timestamp right */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0"
                style={{ background: palette.chipBg, border: `1.5px solid ${palette.border}` }}>
            {palette.icon}
          </span>
          <span className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="text-[14px] font-bold leading-tight" style={{ color: NAVY }}>{headline}</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{ background: palette.pill.bg, color: palette.pill.fg }}>
              {palette.pill.text}
            </span>
          </span>
        </div>
        {eligibility?.checkedAt && (
          <span className="text-[10.5px] shrink-0 mt-0.5" style={{ color: SLATE }}>
            {fmtRelative(eligibility.checkedAt, t)}
          </span>
        )}
      </div>
      {noticeEl}

      {/* Active: copay/deductible/OOP tiles (role-aware copay) + meta line */}
      {resultClass === 'active' && (
        <>
          <div className={`grid gap-2 mt-3 ${twoCopays ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {tiles.map((tile, i) => (
              <div key={i} className="rounded-[11px] px-2.5 py-1.5" style={{ background: ICE }}>
                <div className="text-[8.5px] uppercase tracking-wider font-bold" style={{ color: SLATE }}>{t(tile.labelKey)}{tile.levelKey ? <span className="ml-1 normal-case tracking-normal font-semibold">{t(tile.levelKey)}</span> : null}</div>
                <div className="text-[15px] font-extrabold" style={{ color: NAVY, letterSpacing: '-0.02em' }}>
                  {fmtCents(tile.cents) || '—'}
                </div>
              </div>
            ))}
          </div>
          {/* ★ PROD-587: no "In-network" / "Out of network" here. eligibility.inNetwork is the FIRST
              271 line's benefit-level flag (EB12), not a statement about the doctor on this
              surface (Share, check-in), and not the line any figure above came from. */}
          {eligibility?.planType && (
            <div className="flex items-center gap-1.5 mt-2.5 text-[11px] font-semibold" style={{ color: NAVY }}>
              <span data-plan-type style={{ color: SLATE, fontWeight: 600 }}>
                {String(eligibility.planType).toUpperCase()}
              </span>
            </div>
          )}
        </>
      )}

      {/* Inactive: humanized message + (if available) termination date */}
      {resultClass === 'inactive' && (
        <>
          {body && (
            <p className="text-[12.5px] mt-2 leading-relaxed" style={{ color: NAVY, opacity: 0.85 }}>{body}</p>
          )}
          {eligibility?.terminationDate && (
            <p className="text-[10.5px] mt-1.5" style={{ color: SLATE }}>
              {t('eligibility.planEnded', 'Plan ended')} {eligibility.terminationDate}
            </p>
          )}
        </>
      )}

      {/* Not-found / data-mismatch: humanized body + actionable CTAs */}
      {(resultClass === 'not_found' || resultClass === 'data_mismatch') && (
        <>
          {body && (
            <p className="text-[12.5px] mt-2 mb-2.5 leading-relaxed" style={{ color: NAVY, opacity: 0.85 }}>{body}</p>
          )}
          {showActionableCtas && (onRescanCard || onEditMemberId) && (
            <div className="flex items-center gap-2.5 mt-2 flex-wrap">
              {onRescanCard && (
                <button
                  onClick={onRescanCard}
                  className="inline-flex items-center gap-1.5 text-[12px] font-bold px-3.5 py-2 rounded-full"
                  style={{ background: BLUE, color: '#FFFFFF' }}
                >
                  <ScanLine size={14} /> {t('eligibility.rescanCard')}
                </button>
              )}
              {onEditMemberId && (
                <button
                  onClick={onEditMemberId}
                  className="inline-flex items-center gap-1.5 text-[12px] font-bold px-3.5 py-2 rounded-full"
                  style={{ background: '#FFFFFF', color: BLUE, border: `1.5px solid ${LIGHT}` }}
                >
                  <Pencil size={14} /> {t('eligibility.editMemberId', 'Edit member ID')}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Payer unreachable / config / needs-more-info / payer-not-mapped / unknown */}
      {isInfoState && body && (
        <p className="text-[12.5px] mt-2 leading-relaxed" style={{ color: NAVY, opacity: 0.85 }}>{body}</p>
      )}

      {/* Self-pay: informational message */}
      {resultClass === 'self_pay' && body && (
        <p className="text-[12.5px] mt-2 leading-relaxed" style={{ color: NAVY, opacity: 0.85 }}>{body}</p>
      )}

      {/* Optional re-check link on non-active states. Hidden when actionable CTAs show. */}
      {recheck && resultClass !== 'active' && resultClass !== 'self_pay' && !showActionableCtas && (
        <button
          onClick={recheck}
          className="mt-2.5 inline-flex items-center gap-1.5 text-[12px] font-bold"
          style={{ color: BLUE }}
        >
          <RefreshCw size={13} /> {t('eligibility.recheckCoverage')}
        </button>
      )}
    </div>
  )
}
